/**
 * The four tones, as the pinyin chart names and draws them.
 *
 * The contours are Chao tone numbers, the five-level pitch scale every
 * description of Mandarin tones uses: 55, 35, 214 and 51. The third tone is
 * drawn as 214 because that is how it is said alone, which is how the chart
 * plays it. The notes under the chart say what it does in a word.
 */

/** What the panel says about one tone. */
export interface ToneDescription {
  readonly tone: number;

  /** "First tone". */
  readonly name: string;

  /** "high and level". */
  readonly shape: string;

  /** The pitch, start to end, on Chao's five levels. */
  readonly contour: readonly number[];
}

export const toneDescriptions: readonly ToneDescription[] = [
  { tone: 1, name: "First tone", shape: "high and level", contour: [5, 5] },
  { tone: 2, name: "Second tone", shape: "rising", contour: [3, 5] },
  { tone: 3, name: "Third tone", shape: "low", contour: [2, 1, 4] },
  { tone: 4, name: "Fourth tone", shape: "falling", contour: [5, 1] },
];

/** The SVG namespace, which `createElementNS` needs. */
const svgNamespace = "http://www.w3.org/2000/svg";

/**
 * A small drawing of a tone's pitch, in the current text colour.
 * @param document - The document to draw in.
 * @param contour - The pitch, on Chao's five levels.
 * @returns The drawing, hidden from assistive technology.
 */
export function contourDrawing(
  document: Document,
  contour: readonly number[],
): SVGSVGElement {
  const svg = document.createElementNS(svgNamespace, "svg");
  const width = 36;
  const height = 24;
  // The line keeps its round caps inside the drawing at every level.
  const inset = 3;
  const x = (at: number): number =>
    inset + (at * (width - 2 * inset)) / Math.max(contour.length - 1, 1);
  const y = (level: number): number =>
    inset + ((5 - level) / 4) * (height - 2 * inset);

  svg.setAttribute("viewBox", `0 0 ${String(width)} ${String(height)}`);
  svg.setAttribute("class", "cb-pc-contour");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  for (const level of [5, 1]) {
    const rule = document.createElementNS(svgNamespace, "line");

    rule.setAttribute("x1", "0");
    rule.setAttribute("x2", String(width));
    rule.setAttribute("y1", String(y(level)));
    rule.setAttribute("y2", String(y(level)));
    rule.setAttribute("class", "cb-pc-contour-rule");
    svg.append(rule);
  }

  const line = document.createElementNS(svgNamespace, "polyline");

  line.setAttribute(
    "points",
    contour
      .map((level, at) => `${x(at).toFixed(1)},${y(level).toFixed(1)}`)
      .join(" "),
  );
  line.setAttribute("class", "cb-pc-contour-line");
  svg.append(line);

  return svg;
}
